// =============================================================================
// OmniVoice — realtime protocol helpers (PURE, no DOM / no network)
//
// The omnivoice-gateway (`services/omnivoice-gateway`) is a *transparent* relay:
// it opens the upstream OpenAI Realtime socket with our server-side key, sends
// the `session.update` (pcm16 in/out, server_vad, whisper transcription) itself,
// then forwards every frame verbatim in both directions
// (`services/omnivoice-gateway/src/realtime.ts` ~146-201). So the browser client
// speaks the raw OpenAI Realtime wire protocol — minus auth/session-config which
// the gateway owns.
//
// Transport (derived from the gateway, do NOT assume):
//   • WebSocket, path `/api/omnivoice/realtime`
//     (REALTIME_PATH, realtime.ts:31 + README "WS relay (default)").
//   • Auth: `?token=<supabase_jwt>&tenant_id=<org>` query params
//     (realtime.ts:85-101 — both required; verified via Supabase before any
//     upstream connection is opened).
//   • Audio framing: base64 PCM16, 24 kHz mono — configured server-side in the
//     GA session.update (audio.input/output format {type:'audio/pcm',
//     rate:24000}, realtime.ts).
//   • Turn-taking: server VAD (audio.input.turn_detection server_vad) — the
//     client just streams `input_audio_buffer.append`; the server auto-commits
//     and auto-creates responses. No manual commit / response.create required.
//
// These helpers are pure so they can be unit-tested without a browser.
// =============================================================================

/** WS route on the gateway (must match REALTIME_PATH in the gateway). */
export const REALTIME_PATH = '/api/omnivoice/realtime';

/**
 * OpenAI Realtime PCM sample rate (Hz). The gateway configures pcm16 in/out and
 * OpenAI's Realtime API uses 24 kHz mono PCM16 for both directions.
 */
export const REALTIME_SAMPLE_RATE = 24000;

/** Client → server event types we emit. */
export const CLIENT_EVENT = {
  audioAppend: 'input_audio_buffer.append',
  conversationItemCreate: 'conversation.item.create',
} as const;

/**
 * Server → client event types we consume (relayed verbatim from OpenAI).
 *
 * GA Realtime API names. The gateway relay forwards frames verbatim, so the
 * beta→GA event rename is absorbed HERE (in the client parser), not in the
 * relay. Mapping (beta → GA):
 *   response.audio.delta            → response.output_audio.delta
 *   response.audio.done             → response.output_audio.done
 *   response.audio_transcript.delta → response.output_audio_transcript.delta
 *   response.audio_transcript.done  → response.output_audio_transcript.done
 *   conversation.item.input_audio_transcription.completed → (unchanged in GA)
 * The legacy beta names are still accepted by parseServerEvent (LEGACY_SERVER_
 * EVENT) so a beta-shaped upstream keeps working during migration.
 */
export const SERVER_EVENT = {
  sessionCreated: 'session.created',
  sessionUpdated: 'session.updated',
  audioDelta: 'response.output_audio.delta',
  audioDone: 'response.output_audio.done',
  responseDone: 'response.done',
  assistantTranscriptDelta: 'response.output_audio_transcript.delta',
  assistantTranscriptDone: 'response.output_audio_transcript.done',
  userTranscriptCompleted: 'conversation.item.input_audio_transcription.completed',
  speechStarted: 'input_audio_buffer.speech_started',
  speechStopped: 'input_audio_buffer.speech_stopped',
  error: 'error',
} as const;

/** Legacy (retired beta) names — still parsed for backward compatibility. */
export const LEGACY_SERVER_EVENT = {
  audioDelta: 'response.audio.delta',
  audioDone: 'response.audio.done',
  assistantTranscriptDelta: 'response.audio_transcript.delta',
  assistantTranscriptDone: 'response.audio_transcript.done',
} as const;

/**
 * Build the gateway WebSocket URL. Accepts an http(s) or ws(s) base and
 * normalizes the scheme to ws/wss. `token` and `tenant_id` are required query
 * params (the gateway closes 4400/4401 without them).
 */
export function buildRealtimeUrl(
  baseUrl: string,
  params: { token: string; tenantId: string }
): string {
  const trimmed = baseUrl.trim().replace(/\/+$/, '');
  const wsBase = trimmed.replace(/^http(s?):\/\//i, (_m, s) => `ws${s ? 's' : ''}://`);
  const q = `token=${encodeURIComponent(params.token)}&tenant_id=${encodeURIComponent(
    params.tenantId
  )}`;
  return `${wsBase}${REALTIME_PATH}?${q}`;
}

/** Extract a bearer token from an auth-headers map (case-insensitive). */
export function extractBearerToken(headers: Record<string, string>): string | null {
  const auth = headers.Authorization ?? headers.authorization;
  if (!auth) return null;
  const m = /^Bearer\s+(.+)$/i.exec(auth.trim());
  return m ? m[1].trim() : null;
}

// ---------------------------------------------------------------------------
// PCM / base64 conversions
// ---------------------------------------------------------------------------

/** Clamp a float sample to [-1, 1] and convert to a signed 16-bit int. */
function floatToInt16(sample: number): number {
  const s = Math.max(-1, Math.min(1, sample));
  return s < 0 ? s * 0x8000 : s * 0x7fff;
}

/** Convert mono Float32 PCM (‑1..1) to little-endian Int16 PCM. */
export function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) out[i] = floatToInt16(input[i]);
  return out;
}

/** Convert Int16 PCM back to Float32 (‑1..1) for Web Audio playback. */
export function pcm16ToFloat32(input: Int16Array): Float32Array {
  const out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) out[i] = input[i] / 0x8000;
  return out;
}

/** Base64-encode raw bytes. Uses btoa in the browser, Buffer under Node/tests. */
export function bytesToBase64(bytes: Uint8Array): string {
  if (typeof btoa === 'function') {
    let binary = '';
    const chunk = 0x8000; // avoid arg-count limits on large buffers
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).Buffer.from(bytes).toString('base64');
}

/** Decode base64 to raw bytes. Uses atob in the browser, Buffer under Node. */
export function base64ToBytes(b64: string): Uint8Array {
  if (typeof atob === 'function') {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new Uint8Array((globalThis as any).Buffer.from(b64, 'base64'));
}

/** Encode Float32 mic PCM to a base64 PCM16 string (what the wire expects). */
export function encodePcm16Base64(input: Float32Array): string {
  const pcm = floatToPcm16(input);
  return bytesToBase64(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength));
}

/** Decode a base64 PCM16 string (an audio delta) to Float32 for playback. */
export function decodePcm16Base64(b64: string): Float32Array {
  const bytes = base64ToBytes(b64);
  // Bytes may not be Int16-aligned if the source length is odd; clamp down.
  const usableLen = bytes.byteLength - (bytes.byteLength % 2);
  const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, usableLen / 2);
  return pcm16ToFloat32(int16);
}

/**
 * Linear-resample mono Float32 PCM from `inputRate` to `outputRate`. The mic
 * usually runs at 44.1/48 kHz; the wire expects 24 kHz. Returns the input
 * untouched when the rates match.
 */
export function resampleLinear(
  input: Float32Array,
  inputRate: number,
  outputRate: number
): Float32Array {
  if (inputRate === outputRate || input.length === 0) return input;
  const ratio = inputRate / outputRate;
  const outLength = Math.max(1, Math.round(input.length / ratio));
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const srcPos = i * ratio;
    const i0 = Math.floor(srcPos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = srcPos - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

/** Build the JSON frame that appends captured audio to the input buffer. */
export function buildAudioAppendFrame(base64Pcm16: string): string {
  return JSON.stringify({ type: CLIENT_EVENT.audioAppend, audio: base64Pcm16 });
}

/**
 * Build a frame that injects a live camera observation into the session as a
 * text context item. It is a `conversation.item.create` WITHOUT a following
 * `response.create`, so it does NOT force NEXUS to speak — it just adds what the
 * camera currently sees to the model's context, so NEXUS can reference it when
 * the user next talks ("co widzisz?"). The gateway relays this verbatim to
 * OpenAI Realtime. Vision itself is done server-side (POST /api/nexus/vision),
 * so this stays model-agnostic and works even if gpt-realtime cannot take raw
 * image input.
 */
export function buildVisionContextFrame(description: string): string {
  return JSON.stringify({
    type: CLIENT_EVENT.conversationItemCreate,
    item: {
      type: 'message',
      role: 'user',
      content: [
        {
          type: 'input_text',
          text: `[Widok z kamery użytkownika — analiza na żywo]: ${description}`,
        },
      ],
    },
  });
}

// ---------------------------------------------------------------------------
// Inbound event parsing
// ---------------------------------------------------------------------------

export type RealtimeEvent =
  | { kind: 'session_ready' }
  | { kind: 'audio'; base64: string }
  | { kind: 'audio_done' }
  | { kind: 'response_done' }
  | { kind: 'user_transcript'; text: string }
  | { kind: 'assistant_transcript_delta'; text: string }
  | { kind: 'assistant_transcript_done'; text: string }
  | { kind: 'speech_started' }
  | { kind: 'speech_stopped' }
  | { kind: 'error'; message: string }
  | { kind: 'other'; type: string };

/**
 * Parse one raw server frame (the relayed OpenAI Realtime event) into a
 * normalized union. Defensive: never throws; unknown/garbage → `other`.
 */
export function parseServerEvent(raw: string): RealtimeEvent {
  let evt: Record<string, unknown>;
  try {
    evt = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return { kind: 'other', type: '' };
  }
  const type = typeof evt.type === 'string' ? evt.type : '';

  switch (type) {
    case SERVER_EVENT.sessionCreated:
    case SERVER_EVENT.sessionUpdated:
      // First proof the upstream Realtime session actually exists — the client
      // uses this as its READY signal (connect() resolves only after this).
      return { kind: 'session_ready' };
    case SERVER_EVENT.audioDelta:
    case LEGACY_SERVER_EVENT.audioDelta: {
      const delta = typeof evt.delta === 'string' ? evt.delta : '';
      return delta ? { kind: 'audio', base64: delta } : { kind: 'other', type };
    }
    case SERVER_EVENT.audioDone:
    case LEGACY_SERVER_EVENT.audioDone:
      return { kind: 'audio_done' };
    case SERVER_EVENT.responseDone:
      return { kind: 'response_done' };
    case SERVER_EVENT.userTranscriptCompleted: {
      const text = typeof evt.transcript === 'string' ? evt.transcript.trim() : '';
      return { kind: 'user_transcript', text };
    }
    case SERVER_EVENT.assistantTranscriptDelta:
    case LEGACY_SERVER_EVENT.assistantTranscriptDelta: {
      const text = typeof evt.delta === 'string' ? evt.delta : '';
      return { kind: 'assistant_transcript_delta', text };
    }
    case SERVER_EVENT.assistantTranscriptDone:
    case LEGACY_SERVER_EVENT.assistantTranscriptDone: {
      const text = typeof evt.transcript === 'string' ? evt.transcript.trim() : '';
      return { kind: 'assistant_transcript_done', text };
    }
    case SERVER_EVENT.speechStarted:
      return { kind: 'speech_started' };
    case SERVER_EVENT.speechStopped:
      return { kind: 'speech_stopped' };
    case SERVER_EVENT.error: {
      const err = evt.error as { message?: unknown } | undefined;
      const message = err && typeof err.message === 'string' ? err.message : 'Realtime error';
      return { kind: 'error', message };
    }
    default:
      return { kind: 'other', type };
  }
}

/** WS close codes the gateway uses (realtime.ts) — for friendlier diagnostics. */
export const GATEWAY_CLOSE_CODE: Record<number, string> = {
  4000: 'max session duration reached',
  4400: 'tenant_id required',
  4401: 'unauthorized',
  4403: 'origin not allowed',
  4503: 'omnivoice disabled or not configured',
};

/**
 * Close codes that mean "realtime is not going to work" (auth/config/upstream
 * failure) rather than a normal end of conversation. The voice surface should
 * fall back to the turn-based session on these instead of silently closing.
 */
export const FALLBACK_CLOSE_CODES: ReadonlySet<number> = new Set([4401, 4403, 4503, 1011]);

export function shouldFallbackOnClose(code: number): boolean {
  return FALLBACK_CLOSE_CODES.has(code);
}
